import { AuditLogEntry, AuditLogQuery, AuditLogResponse } from '../models/audit-log';
import api from './api';

export interface AuditLogSummary {
  totalLogs: number;
  timeRange: { startTime: string; endTime: string };
  logTypes: Record<string, number>;
  levels: Record<string, number>;
  recentActivity: AuditLogEntry[];
  urlProcessing: {
    total: number;
    successful: number;
    filtered: number;
    failed: number;
  };
  propertyChanges: {
    created: number;
    updated: number;
    disabled: number;
  };
}

class AuditService {
  /**
   * Get audit logs for a source
   */
  async getAuditLogs(sourceId: string, query?: Partial<AuditLogQuery>): Promise<AuditLogResponse> {
    const params = new URLSearchParams();
    
    if (query?.startTime) params.append('startTime', query.startTime);
    if (query?.endTime) params.append('endTime', query.endTime);
    if (query?.logType) params.append('logType', query.logType);
    if (query?.level) params.append('level', query.level);
    if (query?.limit) params.append('limit', query.limit.toString());
    if (query?.nextToken) params.append('nextToken', query.nextToken);

    const url = `/sources/${sourceId}/audit-logs${params.toString() ? '?' + params.toString() : ''}`;
    
    const response = await api.get(url);
    return response.data;
  }

  /**
   * Get audit log summary for a source
   */
  async getAuditLogSummary(sourceId: string): Promise<AuditLogSummary> {
    const response = await api.get(`/audit-logs/summary/${sourceId}`);
    return response.data;
  }

  /**
   * Get recent audit logs (last 24 hours)
   */
  async getRecentAuditLogs(sourceId: string, limit: number = 50): Promise<AuditLogResponse> {
    const endTime = new Date().toISOString();
    const startTime = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    
    return this.getAuditLogs(sourceId, {
      startTime,
      endTime,
      limit,
    });
  }

  /**
   * Get audit logs by type
   */
  async getAuditLogsByType(
    sourceId: string, 
    logType: AuditLogEntry['logType'], 
    limit: number = 50
  ): Promise<AuditLogResponse> {
    return this.getAuditLogs(sourceId, {
      logType,
      limit,
    });
  }

  /**
   * Get audit logs by level
   */
  async getAuditLogsByLevel(
    sourceId: string, 
    level: AuditLogEntry['level'], 
    limit: number = 50
  ): Promise<AuditLogResponse> {
    return this.getAuditLogs(sourceId, {
      level,
      limit,
    });
  }

  /**
   * Get error logs only
   */
  async getErrorLogs(sourceId: string, limit: number = 50): Promise<AuditLogResponse> {
    return this.getAuditLogsByLevel(sourceId, 'ERROR', limit);
  }

  /**
   * Format log entry for display
   */
  formatLogEntry(log: AuditLogEntry): string {
    const timestamp = new Date(log.timestamp).toLocaleString();
    return `[${timestamp}] ${log.level}: ${log.message}`;
  }

  /**
   * Get log type display name
   */
  getLogTypeDisplayName(logType: AuditLogEntry['logType']): string {
    const displayNames = {
      'SOURCE_PROCESSING': 'Source Processing',
      'URL_PROCESSING': 'URL Processing',
      'PROPERTY_CHANGE': 'Property Change',
      'FILTERING': 'Filtering',
      'ERROR': 'Error',
      'CRON_JOB': 'Cron Job',
      'URL_MANAGEMENT': 'URL Management',
      'SOURCE_MANAGEMENT': 'Source Management',
    };
    return displayNames[logType] || logType;
  }

  /**
   * Get log level display name
   */
  getLogLevelDisplayName(level: AuditLogEntry['level']): string {
    const displayNames = {
      'INFO': 'Info',
      'WARN': 'Warning',
      'ERROR': 'Error',
      'SUCCESS': 'Success',
    };
    return displayNames[level] || level;
  }
}

export const auditService = new AuditService();
